/**
 * メニュー用モジュール。
 * @module app/view/Menu
 */
define([
    'module',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/text!./templates/Menu.html',
    'dojo/topic',
    'dijit/_WidgetBase',
    'dijit/_TemplatedMixin',
    'idis/control/Locator',
    'idis/control/Router',
    'idis/model/UserInfo',
    'idis/consts/ACL',
    // 以下、変数で受けないモジュール
], function (module, declare, lang, template, topic, _WidgetBase, _TemplatedMixin, Locator, Router, UserInfo, ACL) {
    /**
     * リンクの情報を管理するオブジェクト。
     * @typedef LinkInfo
     * @property {string} title リンクのtitle属性（カーソルを合わせたときの表示）
     * @property {string} label リンク文字列
     * @property {string} page ページ識別子、指定時はhrefよりも優先
     * @property {string} href リンク先
     * @property {LinkInfo[]} links リンクの情報の配列
     */

    /**
     * メニュー用オブジェクト
     * @class Menu
     */
    return declare(module.id.replace(/\//g, '.'), [_WidgetBase, _TemplatedMixin],
        /** @lends app/view/Menu~Menu# */ {
            // テンプレート文字列
            templateString: template,

            MENU_TYPE_FLAT: 'フラット型メニュー',
            MENU_TYPE_HIERARCHY: '階層型メニュー',

            menuLabelOpen: '▼ メニュー',
            menuLabelClose: '▶︎ メニュー',

            /**
             * リンク情報の配列
             * @type {module: app/view/Menu~LinkInfo[]}
             */
            _links: null,

            href: null,

            // ユーザーの市町村コード
            _municipalityCd: null,

            // LGWAN側アクセスどうか
            _lgwanFlg: false,

            //
            disasterChgDialog: 'app/view/form/DisasterChangerDialog::initMenu',
            disasterUpd: 'app/disaster/view/DisasterAdminPage::initMenu',
            disasterChg: 'app/view/form/DisasterChanger::initMenu',


            // DOM要素準備後に呼ばれる
            buildRendering: function () {
                this.inherited(arguments);

                this.initMenu();

                // 災害名変更がPubされた際にツリーの情報を更新する
                topic.subscribe(this.disasterChgDialog, lang.hitch(this, function () {
                    this.initMenu();
                }));

                // 災害名管理更新がPubされた際にツリーの情報を更新する
                topic.subscribe(this.disasterUpd, lang.hitch(this, function () {
                    this.initMenu();
                }));

                // 災害名表示がPubされた際にツリーの情報を更新する
                topic.subscribe(this.disasterChg, lang.hitch(this, function () {
                    this.initMenu();
                }));
            },

            // メニュー作成処理
            initMenu: function () {
                this.inherited(arguments);

                var message = UserInfo.getId() + ' は';
                if (UserInfo.getRoleCd() === ACL.ADMIN_USER) {
                    message = message + '管理ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.SYSMGR_USER) {
                    message = message + '全体管理者です。';
                } else if (UserInfo.getRoleCd() === ACL.SHMGR_USER) {
                    message = message + '市本部管理者です。';
                } else if (UserInfo.getRoleCd() === ACL.XXMGR_USER) {
                    message = message + '所属管理者です。';
                } else if (UserInfo.getRoleCd() === ACL.SHIHONBU_USER) {
                    message = message + '市本部ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.KUHONBU_USER) {
                    message = message + '区本部ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.XXHONBU_USER) {
                    message = message + '所属本部ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.IPPAN_USER) {
                    message = message + '一般職員ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.AID_USER) {
                    message = message + '応援職員ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.VPD_USER) {
                    message = message + '自主防災組織ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.WEL_USER) {
                    message = message + '福祉避難所ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.FAC_USER) {
                    message = message + '施設管理者ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.SEW_USER) {
                    message = message + '下水処理事業者ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.ROA_USER) {
                    message = message + '道路管理事業者ユーザです。';
                } else if (UserInfo.getRoleCd() === ACL.REF_USER) {
                    message = message + '参照権限ユーザです。';
                } else {
                    message = message + '未定義ユーザです。 役割コード:' + UserInfo.getRoleCd();
                }
                console.debug(message);
                console.debug('ACL ADMIN : ' + UserInfo.getAcl().ADMIN);
                console.debug('ACL CHIEF ADMIN: ' + UserInfo.getAcl().CHIEF_ADMIN);

                // ユーザーの市町村コードをセット
                // 非ログイン時にgetMunicipalityCds()[0]でエラーが生じるのを回避
                if (UserInfo.getMunicipalityCd()) {
                    this._municipalityCd = UserInfo.getMunicipalityCd();
                }

                // LGWAN側アクセスであるか
                this._lgwanFlg = UserInfo.isLgwan();

                // リンク一覧を生成
                this._links = [];

                if (this.getMenuType() === this.MENU_TYPE_FLAT) {
                    // メニュータイプ：フラット型
                    this._links = this.createMenuTypeFlatLinks();
                } else if (this.getMenuType() === this.MENU_TYPE_HIERARCHY) {
                    // メニュータイプ：階層型
                    this._links = this.createMenuTypeHierarchyLinks();
                } else {
                    // メニュータイプ：階層型
                    this._links = this.createMenuTypeHierarchyLinks();
                    console.warn('メニュータイプが未定義です。 メニュータイプ:' + this.getMenuType());
                }

                this._links.push();
                this.updateLinks();
                this.own(Locator.on('change', lang.hitch(this, 'updateLinks')));
            },

            postCreate: function () {
                this.inherited(arguments);
                // リンクがクリックされたら反応する
                this.on('a:click', lang.hitch(this, function (e) {
                    this.href = e.target.href;
                    var menuLink = false; // ヘッダーに表示されているメニューリンク判定フラグ

                    if (e.target.parentElement.parentElement.id === 'linkList') { // リンクタグの親の親のIDがlinkListの場合
                        // 監視ページはヘッダーにあるがクリックでページ遷移させるためメニューリンクと判定しない
                        if (e.target.text !== '監視ページ') {
                            menuLink = true; // メニューリンクと判定する
                        }

                        // 過去に開いたメニューのエレメントを探す
                        var oldEl = e.target.parentElement.parentElement.querySelector('li.isOpen');

                        if (oldEl) { // 開いたメニューがある場合
                            oldEl.classList.remove('isOpen'); // 閉じる
                        }
                        if (e.target.parentElement !== oldEl) { // 過去に開いたリンクが、今回開いたリンクと異なる場合、
                            e.target.parentElement.classList.add('isOpen'); // メニューを開く
                        }

                    }
                    if (this.href.indexOf(Locator.getHref()) === -1 &&
                        Locator.getHref().indexOf(this.href) === -1) {
                        this.isMenuOpen = false;
                    }
                    if (!Locator.isCurrentPath(this.href)) {
                        // ブラウザーの遷移処理をキャンセル
                        e.preventDefault();
                        if (!menuLink) { // メニューリンクではない場合、遷移先にJump
                            // ページへ遷移
                            Locator.pushState(Locator.getQueryFrom(this.href), true);
                        }
                    }
                    // ページ指定になっていない場合はそのまま通す
                }));

                this.own(Locator.on('change', lang.hitch(this, function () {
                    if (!(this.href === Locator.getHref() ||
                        this.href === Locator.getHref() + '#')) {
                        this.isMenuOpen = false;
                    }
                })));
            },

            updateLinks: function () {

                // 新たなリンク一覧を文字列として構築
                var html = [];
                for (var i = 0; i < this._links.length; i++) {
                    var link = this._links[i];
                    html.push('<li class=""> <a href="');
                    if (link.links) {
                        html.push('#"');
                        if (link.className) {
                            html.push(' class="' + link.className + '"');
                        }
                        html.push('>');
                        html.push(link.label);
                        html.push('</a><ul>');
                        for (var j = 0; j < link.links.length; j++) {
                            var subLink = link.links[j];
                            if (!subLink) {
                                console.log('メニューのリンクが設定されていません。確認してください。');
                                console.log(link.links);
                                continue;
                            }
                            html.push('<li><a href="');
                            if (subLink.external) {
                                // 外部リンクページの場合
                                html.push(subLink.url);
                                // 別タブで開く
                                html.push('" target="_blank" class="u-external is-inverted"');
                            } else {
                                html.push(subLink.page ?
                                    ('?' + Router.PAGE_KEY + '=' + subLink.page) : (subLink.href || '#'));
                            }
                            html.push('" title="');
                            html.push(subLink.title);
                            html.push('">');
                            html.push(subLink.label);
                            html.push('</a></li>');
                        }
                        html.push('</ul></li>');
                    } else {
                        if (link.extlink) {
                            html.push(link.extlink);
                        } else {
                            // 市町ユーザーの場合、メニューから一覧画面へ直接遷移する
                            if (!link.municipalityCd) {
                                html.push(link.page ? ('?' + Router.PAGE_KEY + '=' + link.page) : (link.href || '#'));
                            } else {
                                html.push(link.page ? '?municipalityCd=' + link.municipalityCd +
                                    '&' + Router.PAGE_KEY + '=' + link.page : (link.href || '#'));
                            }
                        }
                        if (link.target) {
                            html.push('" target="');
                            html.push(link.target);
                        }
                        html.push('" title="');
                        html.push(link.title);
                        html.push('">');
                        html.push(link.label);
                        html.push('</a></li>');
                        }
                }
                // リンク一覧の中身を置き換える
                this.linkList.innerHTML = html.join('');
            },

            isMenuOpen: false,

            onMenuLinkClick: function () {
                if (!this.isMenuOpen) {
                    this.isMenuOpen = true;
                } else {
                    this.isMenuOpen = false;
                }
            },

            getMenuType: function () {
                var menuType = '';

                if (UserInfo.getRoleCd() === ACL.ADMIN_USER ||
                    UserInfo.getRoleCd() === ACL.HQ_USER) {
                    menuType = this.MENU_TYPE_HIERARCHY;
                } else if (UserInfo.getRoleCd() === ACL.DEPT_USER ||
                    UserInfo.getRoleCd() === ACL.CITY_H_USER ||
                    UserInfo.getRoleCd() === ACL.CITY_USER ||
                    UserInfo.getRoleCd() === ACL.FIRE_USER ||
                    UserInfo.getRoleCd() === ACL.OTHER_USER) {
                    menuType = this.MENU_TYPE_FLAT;
                } else {
                    // 未定義の役割だった場合は階層型メニュータイプを設定
                    menuType = this.MENU_TYPE_HIERARCHY;
                    console.warn('役割コードが未定義です。 役割コード:' + UserInfo.getRoleCd());
                }

                console.debug('メニュータイプ：' + menuType);

                return menuType;
            },

            // サブメニューリンク
            signageSubLinksLabel: 'デジタルサイネージ',

            signageAdminLink: {
                page: 'signageAdmin', label: 'デジタルサイネージ表示設定管理',
                title: 'デジタルサイネージの設定管理を行います。'
            },

            createMenuTypeHierarchyLinks: function () {

                var links = [];

                // デジタルサイネージ
                {
                    var signageSubLinks = [];

                    if (UserInfo.hasAuthz('F14001') || UserInfo.hasAuthz('F14002')) {
                        // デジタルサイネージ表示設定管理
                        signageSubLinks.push(this.signageAdminLink);
                    }

                    if (signageSubLinks.length > 0) {
                        links.push({ label: this.signageSubLinksLabel, links: signageSubLinks });
                    }
                }

                return links;
            }
        });
});
